/**
 * Platform Logo Utility
 * Extracts domain from website URL and provides logo URLs using reliable services.
 * Falls back to a colored initial when logo is unavailable.
 */

/**
 * Extract clean domain from a URL string.
 * @param {string} url - Full URL like "https://www.fiverr.com/something"
 * @returns {string|null} - Clean domain like "fiverr.com" or null
 */
export function extractDomain(url) {
  if (!url) return null;
  try {
    let cleaned = url.trim();
    if (!cleaned.startsWith('http')) cleaned = 'https://' + cleaned;
    const u = new URL(cleaned);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * Known website builder/hosting domains whose favicons should NOT be used
 * as platform logos (they show the builder's logo, not the actual business).
 */
const BUILDER_DOMAINS = new Set([
  'wix.com', 'wixsite.com', 'squarespace.com', 'wordpress.com', 'wordpress.org',
  'weebly.com', 'godaddy.com', 'shopify.com', 'myshopify.com',
  'carrd.co', 'webflow.io', 'framer.website', 'notion.site',
  'google.com', 'docs.google.com', 'sites.google.com',
  'facebook.com', 'instagram.com', 'twitter.com', 'tiktok.com',
  'youtube.com', 'linkedin.com', 'linktr.ee', 'linktree.com',
  'blogspot.com', 'blogger.com', 'medium.com', 'substack.com',
  'typeform.com', 'jotform.com', 'calendly.com',
  'square.site', 'strikingly.com', 'jimdo.com', 'site123.com',
  'webnode.com', 'duda.co', 'format.com', 'cargo.site',
  'zyro.com', 'mozello.com', 'ucraft.com',
]);

/**
 * Get the logo URL for a platform using Google's favicon service.
 * Returns null if no website, or if the domain is a known builder/host
 * (which would show the builder's logo instead of the actual business logo).
 * @param {string} website - The opportunity's website URL
 * @returns {string|null} - Logo URL or null if no website
 */
export function getPlatformLogoUrl(website) {
  const domain = extractDomain(website);
  if (!domain) return null;

  // Check if the domain or its parent is a known builder
  const parts = domain.split('.');
  for (let i = 0; i < parts.length - 1; i++) {
    const parent = parts.slice(i).join('.');
    if (BUILDER_DOMAINS.has(parent)) return null;
  }

  return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
}

/**
 * Generate a deterministic color from a string (for fallback initials).
 */
export function getInitialColor(name) {
  const colors = [
    'bg-teal-100 text-teal-700',
    'bg-blue-100 text-blue-700',
    'bg-indigo-100 text-indigo-700',
    'bg-violet-100 text-violet-700',
    'bg-rose-100 text-rose-700',
    'bg-amber-100 text-amber-700',
    'bg-emerald-100 text-emerald-700',
    'bg-cyan-100 text-cyan-700',
    'bg-orange-100 text-orange-700',
    'bg-pink-100 text-pink-700',
  ];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

/**
 * Get the first letter(s) for a fallback initial icon.
 */
export function getInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
